import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useBonusEarnResource } from "./useBonusEarnResource";
import { BONUS_EARN_OFFER_HASH } from "@/utilities/constants";

export const useBonusEarnBanner = () => {
  const { t } = useI18nContext();
  // The bonus-earn offer (banner, pill, dialog) is available only to
  // Elite and Premium users with the experiment on.
  const resource = useBonusEarnResource();

  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  // The dialog is driven by the URL hash so it can be deep linked to, e.g. from
  // a marketing email pointing at the homepage (`/#offer`).
  const isDialogOpen = hash === BONUS_EARN_OFFER_HASH;

  const onDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open === isDialogOpen) {
        return;
      }
      if (open) {
        // Pushed so the browser back button closes the dialog.
        navigate(`${pathname}${search}${BONUS_EARN_OFFER_HASH}`);
      } else {
        navigate(`${pathname}${search}`, { replace: true });
      }
    },
    [isDialogOpen, navigate, pathname, search]
  );

  return {
    context: {
      resource,
      isDialogOpen,
      text: resource
        ? t("bonusEarnBanner.text", { earnRate: resource.bonusPointsEarnRate })
        : undefined,
      ctaLabel: t("bonusEarnBanner.cta"),
      ctaAriaLabel: resource
        ? t("bonusEarnBanner.ctaAriaLabel", {
            earnRate: resource.bonusPointsEarnRate,
          })
        : undefined,
    },
    handlers: {
      onViewClick: () => onDialogOpenChange(true),
      onDialogOpenChange,
    },
  };
};
